import { useId, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/cn.js';

/**
 * The small charts a dashboard is made of.
 *
 * Drawn by hand rather than pulled from a charting library, for the same
 * reason the rest of this package is: four shapes at this size cost less
 * than a dependency that ships an axis engine, a tooltip engine and a
 * theme of its own to fight with `theme.css`. Everything here takes its
 * colour from a semantic token, so both themes are one implementation.
 *
 * Bars are HTML boxes, not SVG. A `<svg>` scaled to its container either
 * distorts its stroke widths or needs measuring first; a flex row of divs
 * is crisp at every width, hoverable and focusable for free, and reflows
 * when a panel narrows.
 */

// ── Sparkline ────────────────────────────────────────────────

export interface SparklineProps {
  /** Two or more points. Anything shorter is not a trend and renders nothing. */
  values: readonly number[];
  /** What the shape says, for anyone who cannot see it. */
  label: string;
  /**
   * `accent` draws it in brand yellow. `inherit` takes the colour it is
   * rendered in — which is how a sparkline sits on the yellow tile, where
   * yellow on yellow is nothing and the grey muted token is a smudge.
   */
  tone?: 'accent' | 'inherit';
  className?: string;
}

/**
 * A trend, at the size of a word.
 *
 * Deliberately unlabelled and unscaled: it carries shape only, and sits
 * beside the number it is the history of. The baseline is the series
 * minimum rather than zero, because at 32 pixels tall a flat line near a
 * large zero-based maximum reads as no data at all.
 */
export function Sparkline({ values, label, tone = 'accent', className }: SparklineProps) {
  const gradientId = useId();
  if (values.length < 2) return null;

  const low = Math.min(...values);
  const high = Math.max(...values);
  // A flat series would divide by zero; draw it down the middle instead.
  const span = high - low || 1;
  const step = 100 / (values.length - 1);

  const points = values.map((value, index) => {
    const x = index * step;
    const y = high === low ? 50 : 100 - ((value - low) / span) * 100;
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  });

  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      role="img"
      aria-label={label}
      className={cn('h-8 w-full overflow-visible', tone === 'accent' && 'text-accent', className)}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity={0.24} />
          <stop offset="100%" stopColor="currentColor" stopOpacity={0} />
        </linearGradient>
      </defs>

      <polygon
        points={`0,100 ${points.join(' ')} 100,100`}
        fill={`url(#${gradientId})`}
      />
      <polyline
        points={points.join(' ')}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        /* Without this the 2px stroke is stretched by the viewBox scaling. */
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

// ── Bar chart ────────────────────────────────────────────────

export interface BarDatum {
  key: string;
  /** The axis label. Kept short — it is rotated for nobody. */
  label: string;
  value: number;
  /** The long form, for the tooltip: "Week of 3 June". */
  caption?: string;
}

export interface BarChartProps {
  data: readonly BarDatum[];
  /** Turns a value into the string a person reads. Money, usually. */
  format: (value: number) => string;
  label: string;
  /** Shown in place of the bars when every value is zero. */
  emptyLabel?: string;
  /** At most this many axis labels are drawn; the rest are thinned out. */
  maxAxisLabels?: number;
  className?: string;
}

/**
 * A column chart with one series.
 *
 * Every bucket is drawn, including the empty ones — a gap in trade is the
 * most useful thing a series of earnings can show, and dropping the blanks
 * would put a dead fortnight and a busy one side by side at equal width.
 * An empty bucket keeps a hairline stub so the column stays hoverable and
 * the axis still reads as continuous time.
 */
export function BarChart({
  data,
  format,
  label,
  emptyLabel,
  maxAxisLabels = 7,
  className,
}: BarChartProps) {
  const peak = Math.max(...data.map((datum) => datum.value), 0);
  const isEmpty = peak <= 0;

  // Thin the axis out rather than shrink the type: keep the first, the
  // last, and an even stride between them.
  const stride = Math.max(1, Math.ceil(data.length / maxAxisLabels));

  return (
    <figure className={cn('flex flex-col', className)} aria-label={label}>
      <div className="relative flex h-44 items-end gap-1 sm:gap-1.5">
        {/* The ceiling, so the tallest bar has a value to be read against. */}
        <span className="pointer-events-none absolute -top-1 left-0 text-[11px] font-medium tabular-nums text-text-subtle">
          {isEmpty ? '' : format(peak)}
        </span>
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 border-b border-border-default"
        />

        {isEmpty && emptyLabel ? (
          <p className="absolute inset-0 flex items-center justify-center text-sm text-text-muted">
            {emptyLabel}
          </p>
        ) : null}

        {data.map((datum) => {
          const height = peak > 0 ? (datum.value / peak) * 100 : 0;

          return (
            <div
              key={datum.key}
              tabIndex={0}
              className="group relative flex h-full min-w-0 flex-1 items-end rounded-t-[4px] outline-none focus-visible:ring-2 focus-visible:ring-accent"
              aria-label={`${datum.caption ?? datum.label}: ${format(datum.value)}`}
            >
              <div
                className={cn(
                  'w-full rounded-t-[4px] transition-colors',
                  datum.value > 0
                    ? 'bg-accent group-hover:bg-yellow-600 group-focus-visible:bg-yellow-600 dark:group-hover:bg-yellow-300 dark:group-focus-visible:bg-yellow-300'
                    : 'bg-border-default',
                )}
                /* A percentage of the track, so the chart is height-agnostic.
                   Empty buckets keep 2px of presence rather than vanishing. */
                style={{ height: datum.value > 0 ? `max(3px, ${height}%)` : '2px' }}
              />

              <div
                role="tooltip"
                className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-field border border-border-default bg-surface-raised px-2 py-1 text-xs opacity-0 shadow-lift transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
              >
                <span className="block font-semibold tabular-nums">{format(datum.value)}</span>
                <span className="block text-text-muted">{datum.caption ?? datum.label}</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-2 flex gap-1 sm:gap-1.5" aria-hidden>
        {data.map((datum, index) => (
          <span
            key={datum.key}
            className="min-w-0 flex-1 truncate text-center text-[11px] text-text-subtle"
          >
            {index % stride === 0 || index === data.length - 1 ? datum.label : ' '}
          </span>
        ))}
      </div>
    </figure>
  );
}

// ── Donut ────────────────────────────────────────────────────

export interface DonutSegment {
  key: string;
  label: string;
  value: number;
  /**
   * A Tailwind *text* colour class. The ring is drawn with
   * `stroke="currentColor"`, so one class colours the arc and its legend
   * swatch together and neither can drift from the other.
   */
  colorClassName: string;
}

export interface DonutChartProps {
  segments: readonly DonutSegment[];
  label: string;
  /** The big figure in the hole. */
  centerValue: ReactNode;
  centerLabel: string;
  className?: string;
}

/**
 * A ring, with the total in the middle.
 *
 * One ring of arcs rather than a pie: the hole is where the total goes,
 * and an arc's length is easier to compare than a wedge's angle. Segments
 * worth less than a degree are dropped — an invisible arc with a legend
 * entry is a legend entry that points at nothing.
 */
export function DonutChart({
  segments,
  label,
  centerValue,
  centerLabel,
  className,
}: DonutChartProps) {
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);

  const RADIUS = 42;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

  let consumed = 0;
  const arcs = segments
    .filter((segment) => segment.value > 0)
    .map((segment) => {
      const fraction = segment.value / total;
      const arc = { segment, fraction, offset: consumed };
      consumed += fraction;
      return arc;
    });

  return (
    <div className={cn('flex items-center gap-5', className)}>
      <div className="relative shrink-0">
        <svg viewBox="0 0 100 100" role="img" aria-label={label} className="size-28 -rotate-90">
          <circle
            cx="50"
            cy="50"
            r={RADIUS}
            fill="none"
            strokeWidth={12}
            className="stroke-surface-sunken"
          />
          {arcs.map(({ segment, fraction, offset }) => (
            <circle
              key={segment.key}
              cx="50"
              cy="50"
              r={RADIUS}
              fill="none"
              stroke="currentColor"
              strokeWidth={12}
              strokeLinecap="butt"
              strokeDasharray={`${(fraction * CIRCUMFERENCE).toFixed(3)} ${CIRCUMFERENCE.toFixed(3)}`}
              strokeDashoffset={(-offset * CIRCUMFERENCE).toFixed(3)}
              className={segment.colorClassName}
            >
              <title>{`${segment.label}: ${segment.value}`}</title>
            </circle>
          ))}
        </svg>

        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-display text-2xl font-bold leading-none tabular-nums">
            {centerValue}
          </span>
          <span className="mt-0.5 text-[10px] font-bold uppercase tracking-widest text-text-muted">
            {centerLabel}
          </span>
        </div>
      </div>

      <ul className="min-w-0 flex-1 space-y-1.5">
        {segments
          .filter((segment) => segment.value > 0)
          .map((segment) => (
            <li key={segment.key} className="flex items-center gap-2 text-sm">
              <span
                aria-hidden
                className={cn('size-2.5 shrink-0 rounded-full bg-current', segment.colorClassName)}
              />
              <span className="min-w-0 flex-1 truncate text-text-muted">{segment.label}</span>
              <span className="shrink-0 font-semibold tabular-nums">{segment.value}</span>
            </li>
          ))}
      </ul>
    </div>
  );
}

// ── Ranked bars ──────────────────────────────────────────────

export interface BarListItem {
  key: string;
  label: string;
  /** Under the label — a plate, a count of hires. */
  meta?: string;
  value: number;
  /** An in-app route. Given one, the whole row becomes the target. */
  to?: string;
}

export interface BarListProps {
  items: readonly BarListItem[];
  format: (value: number) => string;
  className?: string;
}

/**
 * A ranking, with the bar drawn behind the label rather than beside it.
 *
 * Behind, because a leaderboard is read down the left edge — putting the
 * bar in its own column pushes the names into a gutter and halves the room
 * a car's name has. Every bar is measured against the leader, so the top
 * row is always full width and the rest are read as fractions of it.
 */
export function BarList({ items, format, className }: BarListProps) {
  const leader = Math.max(...items.map((item) => item.value), 0);

  return (
    <ol className={cn('flex flex-col gap-1', className)}>
      {items.map((item) => {
        const width = leader > 0 ? Math.max(2, (item.value / leader) * 100) : 0;

        const body = (
          <>
            <span
              aria-hidden
              className="absolute inset-y-0 left-0 rounded-field bg-accent-soft transition-[width]"
              style={{ width: `${width}%` }}
            />
            <span className="relative min-w-0 flex-1">
              <span className="block truncate text-sm font-medium">{item.label}</span>
              {item.meta ? (
                <span className="block truncate text-xs text-text-muted">{item.meta}</span>
              ) : null}
            </span>
            <span className="relative shrink-0 text-sm font-semibold tabular-nums">
              {format(item.value)}
            </span>
          </>
        );

        const rowClassName =
          'relative flex items-center gap-3 overflow-hidden rounded-field px-3 py-2';

        return (
          <li key={item.key}>
            {item.to ? (
              <Link
                to={item.to}
                className={cn(
                  rowClassName,
                  'transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                )}
              >
                {body}
              </Link>
            ) : (
              <div className={rowClassName}>{body}</div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

// ── Meter ────────────────────────────────────────────────────

export interface MeterProps {
  /** 0 to 1. Clamped, so a bad input cannot draw outside the track. */
  fraction: number;
  label: string;
  /** The figure shown at the right of the label row. */
  value: string;
  hint?: string;
  /**
   * Track only — for a meter under a tile that has already said what it is
   * measuring. `label` and `value` still describe it to a screen reader,
   * they are just not drawn twice.
   */
  compact?: boolean;
  className?: string;
}

/** One proportion, spelled out — occupancy, a share, a completion. */
export function Meter({ fraction, label, value, hint, compact = false, className }: MeterProps) {
  const percent = Math.round(Math.min(1, Math.max(0, fraction)) * 100);

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {compact ? null : (
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-sm font-medium">{label}</span>
          <span className="text-sm font-semibold tabular-nums">{value}</span>
        </div>
      )}

      <div
        role="progressbar"
        aria-label={label}
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        className={cn(
          'overflow-hidden rounded-pill bg-surface-sunken',
          compact ? 'h-1.5' : 'h-2',
        )}
      >
        <div className="h-full rounded-pill bg-accent" style={{ width: `${percent}%` }} />
      </div>

      {hint && !compact ? <p className="text-xs text-text-muted">{hint}</p> : null}
    </div>
  );
}
